import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { periodRange, secondsToHours } from '../lib/formatHelpers';

/**
 * useManagerTodayData — fetches data scoped to the *current manager's* team
 * for the manager-v2/today page.
 *
 * Sources (Phase 1 / Excel-injected) :
 *  - public.creators            : team filtered by manager_id = current_user
 *  - public.creator_metrics_daily : per-day metrics (sessions, hours, days)
 *                                   for the month-to-date AND today
 *  - public.creator_current_month : tier_current + status snapshot
 *
 * Returns :
 *  - hero     : { activeCreators, totalCreators, sessions, hoursTotal, validGoLiveDays }
 *  - today    : { topToday: array, byServerToday: map, sessionsToday, hoursToday }
 *  - month    : { byServerMonth: map }
 *  - actions  : array<{ creator_id, display_name, kind, server, reason }>
 *  - criticalCount
 *  - loading / error / reload
 *
 * R1 : multi-tenant filter (tenant_id + agency_id + manager_id) appliqué partout.
 * R10 : pagination 1000 rows.
 */
export function useManagerTodayData() {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const { userProfile } = useAuth();
  const [creators, setCreators] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [currentMonth, setCurrentMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!tenantId || !userProfile?.id) return;
    setLoading(true);
    setError(null);
    try {
      const monthRange = periodRange('month');

      // 1) Team roster (creators assigned to this manager)
      let q1 = supabase
        .from('black_creators')
        .select('id, display_name, tiktok_username, server, status, tier, join_time, manager_id, agency_id, tenant_id')
        .eq('tenant_id', tenantId)
        .eq('manager_id', userProfile.id);
      if (agencyId) q1 = q1.eq('agency_id', agencyId);
      const { data: creatorsData, error: e1 } = await q1.limit(1000);
      if (e1) throw e1;

      // 2) Daily metrics for the MTD window (paginated R10)
      const PAGE = 1000;
      const allDaily = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let q = supabase
          .from('black_creator_daily')
          .select('creator_id, snapshot_date, live_streams, live_duration_seconds, valid_go_live_days, new_followers')
          .eq('tenant_id', tenantId)
          .gte('snapshot_date', monthRange.start)
          .lte('snapshot_date', monthRange.end);
        if (agencyId) q = q.eq('agency_id', agencyId);
        const { data, error: e2 } = await q.range(from, from + PAGE - 1);
        if (e2) throw e2;
        const rows = data || [];
        allDaily.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }

      // 3) current month snapshot
      let q3 = supabase
        .from('black_creator_current_month')
        .select('creator_id, tier_current, tier_last_month, days_since_joining, status, server')
        .eq('tenant_id', tenantId)
        .eq('manager_id', userProfile.id);
      if (agencyId) q3 = q3.eq('agency_id', agencyId);
      const { data: currentMonthData, error: e3 } = await q3.limit(1000);
      if (e3) throw e3;

      setCreators(creatorsData || []);
      setDailyRows(allDaily);
      setCurrentMonth(currentMonthData || []);
    } catch (e) {
      console.error('[useManagerTodayData] error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, userProfile?.id]);

  useEffect(() => {
    if (!tenantLoading) fetchAll();
  }, [fetchAll, tenantLoading]);

  // Restrict daily rows to team creators only (server may not have manager filter)
  const teamCreatorIds = useMemo(() => new Set(creators.map((c) => c.id)), [creators]);
  const teamDaily = useMemo(() => dailyRows.filter((r) => teamCreatorIds.has(r.creator_id)), [dailyRows, teamCreatorIds]);

  const todayStr = useMemo(() => periodRange('today').start, []);

  // Aggregate MTD per creator
  const perCreatorMtd = useMemo(() => {
    const map = new Map();
    for (const row of teamDaily) {
      const cur = map.get(row.creator_id) || { sessions: 0, hours: 0, validDays: 0 };
      cur.sessions += row.live_streams || 0;
      cur.hours += secondsToHours(row.live_duration_seconds || 0);
      cur.validDays += row.valid_go_live_days || 0;
      map.set(row.creator_id, cur);
    }
    return map;
  }, [teamDaily]);

  // Aggregate today per creator
  const perCreatorToday = useMemo(() => {
    const map = new Map();
    for (const row of teamDaily) {
      if (row.snapshot_date !== todayStr) continue;
      const cur = map.get(row.creator_id) || { sessions: 0, hours: 0, validDays: 0 };
      cur.sessions += row.live_streams || 0;
      cur.hours += secondsToHours(row.live_duration_seconds || 0);
      cur.validDays += row.valid_go_live_days || 0;
      map.set(row.creator_id, cur);
    }
    return map;
  }, [teamDaily, todayStr]);

  const hero = useMemo(() => {
    let sessions = 0, hours = 0, validDays = 0, active = 0;
    for (const c of creators) {
      const agg = perCreatorMtd.get(c.id);
      if (agg) {
        sessions += agg.sessions;
        hours += agg.hours;
        validDays += agg.validDays;
        if (agg.sessions > 0) active += 1;
      }
    }
    return {
      activeCreators: active,
      totalCreators: creators.length,
      sessions,
      hoursTotal: hours,
      validGoLiveDays: validDays,
    };
  }, [creators, perCreatorMtd]);

  const today = useMemo(() => {
    const list = creators
      .map((c) => {
        const agg = perCreatorToday.get(c.id);
        return agg ? { creator_id: c.id, display_name: c.display_name, server: c.server, sessions: agg.sessions, hours: agg.hours } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.sessions - a.sessions);

    const byServer = {};
    for (const c of creators) {
      const key = c.server || 'unknown';
      byServer[key] = byServer[key] || { sessions: 0, hours: 0, validDays: 0, active: 0, total: 0 };
      byServer[key].total += 1;
      const agg = perCreatorToday.get(c.id);
      if (agg) {
        byServer[key].sessions += agg.sessions;
        byServer[key].hours += agg.hours;
        byServer[key].validDays += agg.validDays;
        if (agg.sessions > 0) byServer[key].active += 1;
      }
    }

    return { topToday: list.slice(0, 5), byServerToday: byServer };
  }, [creators, perCreatorToday]);

  const month = useMemo(() => {
    const byServer = {};
    for (const c of creators) {
      const key = c.server || 'unknown';
      byServer[key] = byServer[key] || { sessions: 0, hours: 0, validDays: 0, active: 0, total: 0 };
      byServer[key].total += 1;
      const agg = perCreatorMtd.get(c.id);
      if (agg) {
        byServer[key].sessions += agg.sessions;
        byServer[key].hours += agg.hours;
        byServer[key].validDays += agg.validDays;
        if (agg.sessions > 0) byServer[key].active += 1;
      }
    }
    return { byServerMonth: byServer };
  }, [creators, perCreatorMtd]);

  // Heuristic action list (no scraping)
  const actions = useMemo(() => {
    const ranges = periodRange('week');
    const startWeek = new Date(ranges.start);
    const out = [];
    for (const c of creators) {
      const agg = perCreatorMtd.get(c.id) || { sessions: 0, validDays: 0 };
      // Onboarding : joined < 14 days ago
      if (c.join_time) {
        const j = new Date(c.join_time);
        const days = Math.floor((Date.now() - j.getTime()) / 86400000);
        if (days >= 0 && days < 14) {
          out.push({ creator_id: c.id, display_name: c.display_name, kind: 'onboarding', server: c.server, reason: `Joined ${days}d ago` });
          continue;
        }
      }
      // Inactive : 0 sessions this week
      const weekRows = teamDaily.filter((r) => r.creator_id === c.id && new Date(r.snapshot_date) >= startWeek);
      const wSessions = weekRows.reduce((s, r) => s + (r.live_streams || 0), 0);
      if (wSessions === 0) {
        out.push({ creator_id: c.id, display_name: c.display_name, kind: 'inactive', server: c.server, reason: 'No sessions this week' });
      }
    }
    return out;
  }, [creators, perCreatorMtd, teamDaily]);

  const criticalCount = actions.filter((a) => a.kind === 'risk' || a.kind === 'inactive').length;

  return {
    hero,
    today,
    month,
    actions,
    criticalCount,
    loading: loading || tenantLoading,
    error,
    reload: fetchAll,
  };
}
